import { useBox, useMounted } from 'shared/hooks/utils';
import { useCallback, useEffect, useState } from 'react';
import { getAPIErrorMessage, useAPI, } from 'api';
import { useCometContext } from 'console/shared/api/comet/comet-context';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { selectDevicesFields } from 'entities/device/lib';
import { useBooleanState } from 'shared/hooks/state/boolean';
export function useFetch(url, call_options = {}, onResponse = null) {
    const isMounted = useMounted();
    const api = useAPI();
    const [isLoading, noLoading] = useBooleanState(true);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const box_call_options = useBox(call_options);
    useEffect(() => {
        (async () => {
            try {
                const response = await api.call(url, box_call_options.current);
                if (response.ok) {
                    if (isMounted.current) {
                        setResponse(response);
                        onResponse?.(response);
                    }
                }
                else {
                    const error = response?.error_ui ?? response?.error ?? 'Не удалось загрузить';
                    if (isMounted.current) {
                        setError(error);
                    }
                }
                if (isMounted.current) {
                    noLoading();
                }
            }
            catch (e) {
                if (isMounted.current) {
                    setError(getAPIErrorMessage(e, 'Не удалось отправить запрос'));
                    noLoading();
                }
                throw e;
            }
        })();
    }, [url]);
    return [isLoading, response, error];
}
export function useGetStorageOrFetchIcon(url) {
    const [responseIcon, setResponse] = useState([]);
    const [errorRun, setErrorRun] = useState(null);
    const [run, isLoading, response, error] = useMutationCallback(url);
    const getFromLocalStorage = useCallback(async (icon_ids) => {
        const icon_from_local_storage = icon_ids.map((id) => {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.indexOf(id) >= 0) {
                    const { title, maintainColor } = JSON.parse(key);
                    return {
                        id,
                        title,
                        maintainColor,
                        icon: localStorage.getItem(key),
                    };
                }
            }
            return id;
        });
        const needed_icons = icon_from_local_storage.filter((item) => typeof item == 'string');
        if (needed_icons.length == 0) {
            setResponse(icon_from_local_storage);
            return;
        }
        const responseFromServer = (await run({
            id_icons: needed_icons,
        }));
        try {
            if (responseFromServer.ok) {
                responseFromServer.icons.forEach((el) => localStorage.setItem(`{"user_icons_id":"${el.id}", "maintainColor":${el.maintainColor}, "title": "${el.title}"}`, atob(el.icon)));
                const result = icon_from_local_storage.map((item) => {
                    if (typeof item == 'string') {
                        const element = responseFromServer.icons.find(({ id }) => id == item);
                        if (element) {
                            return {
                                ...element,
                                icon: atob(element.icon),
                                errorIcon: false,
                            };
                        }
                        else {
                            //если true будет Skeleton вместо иконки
                            return {
                                errorIcon: true,
                            };
                        }
                    }
                    return item;
                });
                setResponse(() => [...result]);
            }
            else {
                alert('Не удалось загрузить');
            }
        }
        catch (e) {
            setErrorRun(e);
        }
    }, []);
    return [responseIcon, getFromLocalStorage, isLoading, error, errorRun];
}
export function useComet(listener) {
    const comet = useCometContext();
    const callback = useBox(listener.callback);
    useEffect(() => {
        const _listener = { ...listener, callback: (msg) => callback.current(msg) };
        comet.listen(_listener);
        return () => comet.unlisten(_listener);
    }, [comet, listener.event, JSON.stringify(listener.condition), listener.once]);
}
export function useMutation(url, data, call_options) {
    const isMounted = useMounted();
    const api = useAPI();
    const devices = useAppSelector(selectDevicesFields(['id', 'online']));
    const [isLoading, noLoading, setLoading] = useBooleanState(false);
    const [is_send_request, , requestSend] = useBooleanState(false);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const box_devices = useBox(devices);
    const box_data = useBox(data);
    const box_call_options = useBox(call_options);
    const run = useCallback(async () => {
        try {
            requestSend();
            setLoading();
            setError(null);
            if (box_data.current) {
                if ('device_id' in box_data.current) {
                    // check if device online
                    const dev = box_devices.current?.find(
                    //@ts-ignore
                    ({ id }) => id == box_data.current.device_id);
                    if (dev && !dev.online) {
                        setError('Прибор не на связи!');
                    }
                }
            }
            const response = await api.call(url, {
                data: box_data.current,
                ...box_call_options.current,
            });
            if (response.ok) {
                if (isMounted.current) {
                    setResponse(response);
                }
            }
            else {
                const error = response?.error_ui ?? response?.error ?? 'Не удалось загрузить';
                if (isMounted.current) {
                    setError(error);
                }
            }
            if (isMounted.current) {
                noLoading();
            }
        }
        catch (e) {
            if (isMounted.current) {
                setError(getAPIErrorMessage(e, 'Не удалось отправить запрос'));
                noLoading();
            }
            throw e;
        }
    }, [url, api, setResponse, setError, noLoading, requestSend]);
    return [run, isLoading, response, error, is_send_request];
}
export function useMutationCallback(url, onResponse = null) {
    const isMounted = useMounted();
    const api = useAPI();
    const [isLoading, noLoading, setLoading] = useBooleanState(false);
    const [is_send_request, , requestSend] = useBooleanState(false);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const run = useCallback(async (data, authenticator) => {
        try {
            requestSend();
            setLoading();
            setError(null);
            const response = await api.call(url, {
                data,
                authenticator,
            });
            if (response.ok) {
                if (isMounted.current) {
                    setResponse(response);
                    onResponse?.(response);
                }
            }
            else {
                const error = response?.error_ui ?? response?.error ?? 'Не удалось загрузить';
                if (isMounted.current) {
                    setError(error);
                }
            }
            if (isMounted.current) {
                noLoading();
            }
            return response;
        }
        catch (e) {
            if (isMounted.current) {
                console.log('e', e);
                setError(getAPIErrorMessage(e, 'Не удалось отправить запрос'));
                noLoading();
            }
            throw e;
        }
    }, [url, api, setResponse, setError, noLoading, requestSend]);
    return [run, isLoading, response, error, is_send_request];
}
